import { IllustrationHubName } from "@assets/IllustrationHubName";
import { KeyboardAwareDrawer, KeyboardAwareDrawerContent } from "@components/KeyboardAwareDrawer";
import { ScannerMulti, type ScannerMultiRef } from "@features/Scanner/ScannerMulti";
import { useBackButtonHijack } from "@hooks/useBackButtonHijack";
import { useI18nContext } from "@i18n/i18n-react";
import { T } from "@i18n/T";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import { AspectRatio } from "@shadcn/ui/aspect-ratio";
import { Button } from "@shadcn/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@shadcn/ui/dialog";
import { DrawerTitle } from "@shadcn/ui/drawer";
import { Input } from "@shadcn/ui/input";
import { Label } from "@shadcn/ui/label";
import { compact, uniq } from "es-toolkit";
import { SwitchCamera } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useRelayEnvironment } from "react-relay";
import { fetchQuery, graphql } from "relay-runtime";
import { toast } from "sonner";
import { useHubSession } from "@contexts/HubSessionContext";
import { isHubQrUrl } from "@utils/hubQr";
import { HubScannerDrawerQuery } from "./__generated__/HubScannerDrawerQuery.graphql";
import { AppHeader } from "./layout/AppHeader";

type HubScannerDrawerProps = {
	isOpen: boolean;
	onClose: () => void;
	expectedHubId?: string;
	/** Side the page needs. Another side of the hub still connects: the page then sends the user there. */
	expectedSide?: string;
	onHubConnected?: (hub: { id: string; name: string; side?: "A" | "B" }) => void;
};

const query = graphql`
	query HubScannerDrawerQuery($hubName: String!) {
		hubMeta(by: { name: $hubName }) {
			id
			hub @required(action: THROW) {
				id
				spaces {
					id
					frame
				}
			}
			name
		}
	}
`;

export const HubScannerDrawer = (props: HubScannerDrawerProps) => {
	const { isOpen, onClose, expectedHubId, onHubConnected } = props;
	const [isManualEntryOpen, setIsManualEntryOpen] = useState(false);
	const [manualHubName, setManualHubName] = useState("");
	const { setHub } = useHubSession();
	const environment = useRelayEnvironment();
	const [isLoading, setIsLoading] = useState(false);
	const [hasCameraPermission, setHasCameraPermission] = useState(false);
	const scannerRef = useRef<ScannerMultiRef>(null);
	const lastScannedRef = useRef<{ url: string; time: number } | null>(null);
	const i18n = useI18nContext().LL["new-ui"];

	useEffect(() => {
		let stale = false;
		navigator.permissions
			.query({ name: "camera" })
			.then((result) => {
				if (stale) return;
				setHasCameraPermission(result.state === "granted");
			})
			.catch((e) => {
				toast.error(i18n.FAILED_CHECK_CAMERA(), { description: e.message });
			});
		return () => {
			stale = true;
		};
	}, []);

	const getHub = async (hubName: string) => {
		setIsLoading(true);
		const data = await fetchQuery<HubScannerDrawerQuery>(environment, query, { hubName })
			.toPromise()
			.finally(() => setIsLoading(false));
		return data?.hubMeta
			? {
					id: data.hubMeta.hub.id,
					name: data.hubMeta.name,
					sides: uniq(compact(data.hubMeta.hub.spaces.map((s) => s.frame.at(0)))),
				}
			: undefined;
	};

	const handleManualSubmit = async () => {
		const [hubName, typedSide] = manualHubName.trim().split("-");
		// A hub name without a side ("harry") means its A side, like the hub's own label.
		const side = typedSide || "A";
		if (!hubName) {
			toast.error(i18n.INVALID_HUB_NAME());
			return;
		}
		const hub = await getHub(hubName);
		if (!hub) {
			toast.error(i18n.UNKNOWN_HUB_NAME());
			return;
		}
		if (expectedHubId && hub.id !== expectedHubId) {
			toast.error(i18n.WRONG_HUB());
			return;
		}

		/**
		 * Hubs without sides always have a default A side, so if there are 2, it means it actually has
		 * sides printed on the hub
		 */
		const isMultiSideHub = hub.sides.length > 1;

		// For multi-side hubs, require a valid side (if side is provided)
		if (isMultiSideHub && side && !hub.sides.map((s) => s.toLowerCase()).includes(side.toLowerCase())) {
			toast.error(i18n.UNKNOWN_SIDE());
			return;
		}

		// A side other than `expectedSide` still connects the user: the page then asks them to go
		// to the other side of the hub instead of rejecting the scan.
		const hubData = { id: hub.id, name: hub.name, side: side.toUpperCase() as "A" | "B" };
		setHub(hubData);
		if (onHubConnected) {
			onHubConnected(hubData);
		}
		onClose();
		setIsManualEntryOpen(false);
		setManualHubName("");
	};

	useBackButtonHijack(isOpen, () => {
		setIsManualEntryOpen(false);
		onClose();
	});

	return (
		<>
			<KeyboardAwareDrawer
				open={isOpen}
				onOpenChange={(open) => {
					if (!open) {
						onClose();
					}
				}}
				shouldScaleBackground
			>
				<KeyboardAwareDrawerContent className="max-h-full! h-full overflow-hidden">
					<VisuallyHidden>
						<DrawerTitle>
							<T>{i18n.SCAN_QR_CODE()}</T>
						</DrawerTitle>
					</VisuallyHidden>
					<div className="space-y-4 px-6 overflow-y-scroll pointer-events-all pb-22 -mt-6">
						<div className="-mx-6">
							<AppHeader showHelpButton={true} />
						</div>
						<div className="flex items-center justify-between">
							<h2 className="text-2xl leading-8 font-breit">
								<T>{i18n.SCAN_QR_CODE()}</T>
							</h2>
							{hasCameraPermission && (
								<Button
									variant="outline"
									size="icon"
									onClick={async () => {
										await scannerRef.current?.switchCamera();
									}}
								>
									<SwitchCamera className="h-5 w-5" />
								</Button>
							)}
						</div>
						<AspectRatio ratio={342 / 404} className="rounded-1.5 overflow-hidden">
							{hasCameraPermission ? (
								<>
									<ScannerMulti
										ref={scannerRef}
										className="w-full h-full object-cover"
										scanner="jsqr-preprocess"
										onScannerChange={() => {}}
										onResult={async (result: { data: string }) => {
											if (isLoading) {
												return;
											}
											const text = result.data;

											// Dedup: skip if same QR scanned within 3s
											const now = Date.now();
											if (lastScannedRef.current && lastScannedRef.current.url === text && now - lastScannedRef.current.time < 3000) {
												return;
											}
											lastScannedRef.current = { url: text, time: now };

											try {
												// Text is e.g. https://qr.bacehub.de/buexe-a or https://qr.staging.bacehub.de/buexe-a
												// For one-side hubs, it can also be just https://qr.bacehub.de/buexe
												const url = new URL(text);

												if (!isHubQrUrl(url)) {
													toast.error(i18n.UNKNOWN_QR_CODE(), {
														description: i18n.QR_NOT_BACE(),
													});
													return;
												}

												const [hubSlug, scannedSide] = url.pathname.slice(1).split("-");
												const side = scannedSide || "A";
												if (!hubSlug) {
													toast.error(i18n.FAILED_PARSE_QR());
													return;
												}

												const hub = await getHub(hubSlug);
												if (!hub) {
													toast.error(i18n.WRONG_HUB());
													return;
												}

												if (expectedHubId && hub.id !== expectedHubId) {
													toast.error(i18n.WRONG_HUB());
													return;
												}

												const isMultiSideHub = hub.sides.length > 1;

												// For multi-side hubs, require a valid side (if side is provided)
												if (isMultiSideHub && side && !hub.sides.map((s) => s.toLowerCase()).includes(side.toLowerCase())) {
													toast.error(i18n.UNKNOWN_SIDE());
													return;
												}

												const hubData = { id: hub.id, name: hub.name, side: side.toUpperCase() as "A" | "B" };
												setHub(hubData);
												if (onHubConnected) {
													onHubConnected(hubData);
												}
												onClose();
											} catch {
												toast.error(i18n.UNKNOWN_QR_CODE());
												return;
											}
										}}
									/>
									{isLoading && (
										<div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
											<div className="flex flex-col items-center gap-2">
												<div className="h-8 w-8 animate-spin rounded-full border-4 border-white border-t-transparent" />
												<span className="text-sm text-white font-medium">{i18n.CONNECTING()}</span>
											</div>
										</div>
									)}
								</>
							) : (
								<div className="w-full h-full flex items-center justify-center">
									<Button
										className="font-breit"
										onClick={() => {
											navigator.mediaDevices
												.getUserMedia({ video: true })
												.then(() => {
													setHasCameraPermission(true);
												})
												.catch((e) => {
													toast.error(i18n.FAILED_CAMERA_ACCESS(), { description: e.message });
												});
										}}
									>
										<T>{i18n.ALLOW_CAMERA_ACCESS()}</T>
									</Button>
								</div>
							)}
						</AspectRatio>
					</div>
					<div className="fixed bottom-0 left-0 right-0 m-6 md:static md:w-full">
						<Button className="w-full font-breit" size="lg" onClick={() => setIsManualEntryOpen(true)}>
							<T>{i18n.ENTER_HUB_NAME_INSTEAD()}</T>
						</Button>
					</div>
				</KeyboardAwareDrawerContent>
			</KeyboardAwareDrawer>

			<Dialog open={isManualEntryOpen} onOpenChange={setIsManualEntryOpen}>
				<DialogContent>
					<DialogHeader>
						<DialogTitle>
							<T>{i18n.ENTER_HUB_NAME()}</T>
						</DialogTitle>
						<DialogDescription>{i18n.HUB_NAME_LOCATION()}</DialogDescription>
						<IllustrationHubName className="w-full h-full" />
					</DialogHeader>
					<div className="space-y-4 py-4">
						<form
							id="manual-form"
							className="space-y-2"
							onSubmit={(e) => {
								e.preventDefault();
								handleManualSubmit();
							}}
						>
							<Label htmlFor="hub-name">
								<T>{i18n.HUB_NAME()}</T>
							</Label>
							<Input
								id="hub-name"
								placeholder={i18n.ENTER_HUB_NAME_HERE()}
								value={manualHubName}
								onChange={(e) => setManualHubName(e.target.value)}
							/>
						</form>
					</div>
					<DialogFooter>
						<Button form="manual-form" type="submit" className="w-full">
							<T>{i18n.CONNECT_TO_HUB_BUTTON()}</T>
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</>
	);
};
